import React from 'react';
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { MenuHealthAnalysis, MenuHealthIssue } from '@/services/menuHealth';

interface MenuHealthPanelProps {
  analysis: MenuHealthAnalysis;
  onFix?: (issueKey: MenuHealthIssue['key']) => void;
}

const formatLabel = (issue: MenuHealthIssue) => issue.label;

export const MenuHealthPanel: React.FC<MenuHealthPanelProps> = ({ analysis, onFix }) => {
  const hasIssues = analysis.issues.some((i) => i.count > 0);
  const sorted = [...analysis.issues].sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 flex items-center justify-between border-b border-slate-100">
        <div>
          <div className="text-xs font-bold uppercase text-slate-500">Menu Health</div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-extrabold text-slate-900">{analysis.score}</span>
            <span className="text-xs font-semibold text-slate-500">/100</span>
          </div>
          <div className="text-xs text-slate-500">
            Categories: {analysis.totals.categories} • Items: {analysis.totals.items}
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-bold ${hasIssues ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
          {hasIssues ? 'Needs attention' : 'Healthy'}
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {sorted.map((issue) => (
          <div key={issue.key} className="p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {issue.count > 0 ? (
                <AlertCircle className="w-4 h-4 text-amber-600" />
              ) : (
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              )}
              <div>
                <div className="text-sm font-semibold text-slate-800">{formatLabel(issue)}</div>
                <div className="text-xs text-slate-500">{issue.count} issue{issue.count === 1 ? '' : 's'}</div>
              </div>
            </div>
            <button
              disabled={!issue.count}
              onClick={() => issue.count && onFix?.(issue.key)}
              className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${
                issue.count
                  ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                  : 'border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50'
              }`}
            >
              Fix <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
